/**
 * Extractions Service
 *
 * Handles adding survey variables to a user's custom extraction list
 * via the backend API. Each variable is stored as a row in the
 * chat-session-custom-extractions DynamoDB table, keyed by session ID
 * and a composite survey+variable identifier.
 */

// Cached API base URL loaded from aws-exports.json
let EXTRACTIONS_API_URL = ""

/**
 * Load the extractions API URL from aws-exports.json configuration.
 *
 * Falls back to a relative /api/extractions path for local development
 * when no explicit API URL is configured.
 *
 * @returns The full URL for the extractions API endpoint.
 */
async function loadApiUrl(): Promise<string> {
  if (EXTRACTIONS_API_URL) {
    return EXTRACTIONS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = import.meta.env.VITE_API_URL || config.apiUrl || ""
    EXTRACTIONS_API_URL = baseUrl
      ? `${baseUrl}/api/extractions`
      : "/api/extractions"
    return EXTRACTIONS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Extractions API URL not configured")
  }
}

/**
 * Payload sent to the API when adding a variable to the extraction.
 */
export interface AddToExtractionPayload {
  /** The chat session UUID (partition key). */
  sessionId: string
  /** The survey identifier (e.g. "KE2020DHS"). */
  surveyId: string
  /** The variable name (e.g. "hv105"). */
  variableName: string
  /** The survey program name (e.g. "DHS", "LSMS"). */
  programName: string
  /** Human-readable label for the variable. */
  variableLabel: string
  /** The source data file name (e.g. "KEHR7AFL.SAV"). */
  fileName: string
  /** S3 URI pointing to the source data file for this variable. */
  s3URI: string
  /** All value/description pairs for the variable. */
  values: Array<{ value: string; valueDescription: string }>
  /** Full country name (resolved from surveys table for LSMS). */
  countryName?: string
  /** The survey year (e.g. "2020"). */
  surveyYear?: string
  /** MICS round identifier (only present for MICS surveys). */
  round?: string
  /** Unit of analysis code (recode for DHS/SPA, unitofanalysis for LSMS). */
  unitOfAnalysis?: string
}

/**
 * Add a survey variable to the user's custom extraction for a given session.
 *
 * Sends a PUT request — if the variable already exists in the extraction,
 * the backend returns success without modifying the existing row (idempotent).
 *
 * @param payload - The variable metadata to store.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True if the variable was added (or already existed), throws on failure.
 */
export async function addToExtraction(
  payload: AddToExtractionPayload,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(apiUrl, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to add variable to extraction: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}

/**
 * A single variable entry within a batch add request. Same shape as the
 * single-add payload minus the sessionId, which is shared across the batch.
 */
export type BatchExtractionVariable = Omit<AddToExtractionPayload, "sessionId">

/**
 * Payload sent to the API when adding multiple variables to the extraction
 * in one request.
 */
export interface BatchAddToExtractionPayload {
  /** The chat session UUID (partition key, shared by every variable). */
  sessionId: string
  /** The variables to add. Must be non-empty. */
  variables: BatchExtractionVariable[]
}

/**
 * Result summary returned by the batch add endpoint.
 */
export interface BatchAddToExtractionResult {
  /** Number of variables newly written to the extraction. */
  added: number
  /** Number of variables skipped because they were already in the extraction. */
  skipped: number
}

/**
 * Add multiple survey variables to a session's custom extraction in one call.
 *
 * Sends a PUT request to /api/extractions/batch. The backend writes as many
 * of the supplied variables as it can (non-atomic) and skips any that already
 * exist, so re-sending already-added variables is safe and never overwrites
 * existing rows.
 *
 * @param payload - The session ID and the list of variables to add.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns A summary of how many variables were added vs. skipped.
 * @throws If the request fails (non-2xx response).
 */
export async function batchAddToExtraction(
  payload: BatchAddToExtractionPayload,
  accessToken: string
): Promise<BatchAddToExtractionResult> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/batch`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to batch add variables to extraction: HTTP ${response.status} — ${errorBody}`
    )
  }

  return response.json()
}

/**
 * Payload sent to the API when removing a single variable from the extraction.
 */
export interface RemoveFromExtractionPayload {
  /** The chat session UUID. */
  sessionId: string
  /** The survey identifier (e.g. "KE2020DHS"). */
  surveyId: string
  /** The source data file name (e.g. "KEHR7AFL.SAV"). */
  fileName: string
  /** The variable name (e.g. "hv105"). */
  variableName: string
}

/**
 * Remove a single variable from a session's custom extraction.
 *
 * Sends a DELETE request to /api/extractions with the composite key fields
 * in the body. If the variable doesn't exist, the backend returns success
 * (idempotent delete).
 *
 * @param payload - The variable identity to remove.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True on success, throws on failure.
 */
export async function removeFromExtraction(
  payload: RemoveFromExtractionPayload,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(apiUrl, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to remove variable from extraction: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}

/**
 * Payload sent to the API when updating variable recodes (label rename and/or value recodes).
 */
export interface UpdateVariableRecodesPayload {
  /** The chat session UUID. */
  sessionId: string
  /** The survey identifier. */
  surveyId: string
  /** The source data file name. */
  fileName: string
  /** The variable name. */
  variableName: string
  /** The new label override. Pass an empty string to revert to the original label. Optional. */
  variableLabelRename?: string
  /**
   * Map of original value → recoded description. Pass an empty object to clear
   * all recodes. Multiple values can map to the same description. Optional.
   */
  valueRecode?: Record<string, string>
}

/**
 * Update editable metadata on an existing extraction variable.
 *
 * Sends a PATCH request to /api/extractions with the variable identity and
 * any combination of:
 *   - variableLabelRename: overrides the variable label
 *   - valueRecode: maps original values to new descriptions
 *
 * At least one of the two fields must be provided.
 *
 * @param payload - The variable identity and metadata updates.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True on success, throws on failure.
 */
export async function updateVariableRecodes(
  payload: UpdateVariableRecodesPayload,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(apiUrl, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to update variable metadata: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}

/**
 * Clear all variables from a session's custom extraction.
 *
 * Sends a DELETE request to /api/extractions/{sessionId} which removes
 * every extraction row for that session. Called when a session is deleted
 * so orphaned extraction data doesn't linger.
 *
 * @param sessionId - The chat session UUID whose extraction should be cleared.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True on success, throws on failure.
 */
export async function clearExtraction(
  sessionId: string,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to clear extraction: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}

/**
 * A single variable entry in the extraction list.
 */
export interface ExtractionVariable {
  /** The chat session UUID. */
  sessionId: string
  /** Composite sort key: "{surveyId}#{fileName}#{variableName}". */
  sortKey: string
  /** The survey identifier. */
  surveyId: string
  /** The source data file name. */
  fileName: string
  /** The variable name. */
  variableName: string
  /** The survey program name. */
  programName: string
  /** Human-readable label for the variable. */
  variableLabel: string
  /** User-provided label override. When set, displayed instead of variableLabel. */
  variableLabelRename?: string
  /**
   * Map of original value → recoded description. Allows users to remap/group
   * value descriptions
   */
  valueRecode?: Record<string, string>
  /** S3 URI pointing to the source data file. */
  s3URI: string
  /** TTL epoch timestamp. */
  expiresAt: number
  /** Value/description pairs (may be absent if no values). */
  values?: Array<{ value: string; valueDescription: string }>
}

/**
 * List all variables in a session's custom extraction.
 *
 * Sends a GET request to /api/extractions/{sessionId} which returns all
 * variables that have been added to the extraction for that session.
 *
 * @param sessionId - The chat session UUID to list extractions for.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Array of extraction variables for the session.
 */
export async function listExtraction(
  sessionId: string,
  accessToken: string
): Promise<ExtractionVariable[]> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to list extraction: HTTP ${response.status} — ${errorBody}`
    )
  }

  const data = await response.json()
  return data.variables ?? []
}

/**
 * Response from the extraction status endpoint.
 *
 * Answers the question "does extraction output exist for this session?" —
 * used to determine whether the button shows "Create" or "Update".
 */
export interface ExtractionStatusResponse {
  /** Whether extraction output files exist for this session. */
  exists: boolean
  /** The S3 prefix where extraction files are stored (only present when exists=true). */
  s3Prefix?: string
}

/**
 * Response from the extraction progress endpoint.
 *
 * Reports the progress of an in-flight extraction job. Polled by the
 * frontend while waiting for a result.
 *
 * - null: No extraction job has been submitted for this session.
 * - "IN_PROGRESS": The extraction is currently running.
 * - "COMPLETED": The extraction finished successfully.
 * - "FAILED": The extraction encountered an error.
 */
export interface ExtractionProgressResponse {
  /** Current extraction status: "IN_PROGRESS", "COMPLETED", "FAILED", or null. */
  status: "IN_PROGRESS" | "COMPLETED" | "FAILED" | null
  /** The S3 prefix where extraction files are stored (only present when COMPLETED). */
  s3Prefix?: string
  /** Error message from the extraction Lambda (only present when FAILED). */
  error?: string
  /** Error type/class (only present when FAILED). */
  errorType?: string
  /** extraction lambda response with per-file status (only present when COMPLETED). */
  result?: {
    extraction_id: string
    extraction_s3_prefix: string
    files: Array<{
      surveyId: string
      fileName: string
      outputFileName: string
      status: string
    }>
  }
}

/**
 * Check whether an extraction has already been created for a session.
 *
 * Sends a GET request to /api/extractions/{sessionId}/status which checks
 * the cross-account S3 bucket for output files under the session's prefix
 * (excludes the _status.json marker). Used to determine whether the UI
 * should show "Create" or "Update".
 *
 * @param sessionId - The chat session UUID to check.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Whether extraction output exists and its S3 prefix.
 */
export async function getExtractionStatus(
  sessionId: string,
  accessToken: string
): Promise<ExtractionStatusResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/status`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to check extraction status: HTTP ${response.status} — ${errorBody}`
    )
  }

  return response.json()
}

/**
 * Poll the progress of an in-flight extraction.
 *
 * Sends a GET request to /api/extractions/{sessionId}/progress which reads
 * the _status.json marker file from S3. Returns the current state of the
 * extraction job (IN_PROGRESS, COMPLETED, FAILED, or null if never started).
 *
 * Only call this while actively waiting for an extraction to finish.
 *
 * @param sessionId - The chat session UUID to check progress for.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The extraction progress with status, result, or error information.
 */
export async function getExtractionProgress(
  sessionId: string,
  accessToken: string
): Promise<ExtractionProgressResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/progress`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to check extraction progress: HTTP ${response.status} — ${errorBody}`
    )
  }

  return response.json()
}

/**
 * Response from the create extraction endpoint (async).
 *
 * The extraction is processed asynchronously — the backend fires the
 * cross-account Lambda and returns 202 immediately. Poll
 * getExtractionStatus to detect when the extraction output appears in S3.
 */
export interface CreateExtractionResponse {
  /** Confirmation message from the backend. */
  message: string
  /** The extraction ID (same as sessionId). */
  extraction_id: string
  /** Number of files being extracted. */
  files_count: number
  /** Total number of variables being extracted. */
  variables_count: number
}

/**
 * Submit an extraction job for a session (async).
 *
 * Sends a POST request to /api/extractions/{sessionId}/create which reads
 * the session's variables from DynamoDB, groups them by file, and fires the
 * cross-account extraction Lambda asynchronously. Returns a 202 Accepted
 * immediately — poll getExtractionStatus to detect completion.
 *
 * @param sessionId - The chat session UUID to create the extraction for.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Confirmation that the extraction job was submitted.
 */
export async function createExtraction(
  sessionId: string,
  accessToken: string
): Promise<CreateExtractionResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/create`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
  })

  // Accept both 200 and 202 as success (202 is the expected async response)
  if (!response.ok && response.status !== 202) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to create extraction: HTTP ${response.status} — ${errorBody}`
    )
  }

  return response.json()
}

// ---------------------------------------------------------------------------
// File Names
// ---------------------------------------------------------------------------

/**
 * A single file name record stored in DynamoDB.
 * Each record represents the user's chosen CSV output name for a specific
 * source file within a session's extraction.
 *
 * Partition key: sessionId
 * Sort key: "file#{surveyId}#{fileName}"
 */
export interface FileInfoRecord {
  /** The chat session UUID (partition key). */
  sessionId: string
  /** Composite sort key: "file#{surveyId}#{fileName}". */
  sortKey: string
  /** The survey identifier (e.g. "KE2020DHS"). */
  surveyId: string
  /** The source data file name (e.g. "KEHR7AFL.SAV"). */
  fileName: string
  /** The user-specified output CSV file name (e.g. "my_output.csv"). */
  outputFileName: string
  /** Unit of analysis code (recode for DHS/SPA, unitofanalysis for LSMS). */
  unitOfAnalysis?: string
}

/**
 * Payload sent to the API when setting/updating an output file name.
 */
export interface SaveFileInfoPayload {
  /** The chat session UUID. */
  sessionId: string
  /** The survey identifier. */
  surveyId: string
  /** The source data file name. */
  fileName: string
  /** The desired output CSV file name. */
  outputFileName: string
}

/**
 * Set or update the output CSV file name for a specific source file in a session.
 *
 * Sends a PUT request to /api/extractions/{sessionId}/file which
 * creates or updates a single output name row in DynamoDB, keyed by
 * sessionId + "file#{surveyId}#{fileName}".
 *
 * @param payload - The output file name metadata to store.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns True on success, throws on failure.
 */
export async function saveFileInfo(
  payload: SaveFileInfoPayload,
  accessToken: string
): Promise<boolean> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${payload.sessionId}/file`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      surveyId: payload.surveyId,
      fileName: payload.fileName,
      outputFileName: payload.outputFileName,
    }),
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to set output file name: HTTP ${response.status} — ${errorBody}`
    )
  }

  return true
}

/**
 * Retrieve all output file name records for a session.
 *
 * Sends a GET request to /api/extractions/{sessionId}/file which
 * returns all output name rows stored for that session.
 *
 * @param sessionId - The chat session UUID.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Array of output file name records for the session.
 */
export async function listFileInfo(
  sessionId: string,
  accessToken: string
): Promise<FileInfoRecord[]> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/file`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to list output file names: HTTP ${response.status} — ${errorBody}`
    )
  }

  const data = await response.json()
  return data.outputNames ?? []
}

// ---------------------------------------------------------------------------
// Survey Info
// ---------------------------------------------------------------------------

/**
 * A survey metadata record stored in DynamoDB.
 * Stores survey-level display data (countryName, surveyYear, round, programName)
 * separately from individual variable rows.
 *
 * Partition key: sessionId
 * Sort key: "survey#{surveyId}"
 */
export interface SurveyInfoRecord {
  /** The survey identifier (e.g. "KE2020DHS"). */
  surveyId: string
  /** The survey program name (e.g. "DHS"). */
  programName: string
  /** Full country name. */
  countryName: string
  /** The survey year (e.g. "2020"). */
  surveyYear: string
  /** MICS round identifier (only present for MICS surveys). */
  round: string
}

/**
 * Retrieve all survey metadata records for a session.
 *
 * Sends a GET request to /api/extractions/{sessionId}/survey which
 * returns all survey info rows stored for that session.
 *
 * @param sessionId - The chat session UUID.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns Array of survey info records for the session.
 */
export async function listSurveyInfo(
  sessionId: string,
  accessToken: string
): Promise<SurveyInfoRecord[]> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/${sessionId}/survey`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const errorBody = await response.text()
    throw new Error(
      `Failed to list survey info: HTTP ${response.status} — ${errorBody}`
    )
  }

  const data = await response.json()
  return data.surveys ?? []
}
